import { useLocalization } from "@/app/hook/use-localization";
import { LocalizationText } from "@/app/index";
import { Button, Flex, FormItem, InputSearch } from "@/base/components/index";
import { PlusIcon } from "@/base/static/index";
import { useFormWatch } from "@/base/utils/index";
import { ButtonLink } from "@/components/button-link/components/button-link";
import { CheckboxGroupList } from "@/components/checkbox-group-list/components/checkbox-group-list";
import { DeploymentBusinessUnitItemType } from "@/schemas/index";
import { useEffect, useMemo, useState } from "react";
import { useContextModalHardwareInstall } from "../context/modal-hardware-install-context";
import { useCrSelected } from "../hooks/use-cr-selected";

export const ModalHardwareInstallBusinessUnitSelectActions = () => {
  const [submittable, setSubmittable] = useState<boolean>(false);
  const localization = useLocalization();
  const isCrSelect = useCrSelected();
  const { form, deploymentData, hardwareItem, fetchChangeDrawerPage } =
    useContextModalHardwareInstall();

  const values = useFormWatch([], form);

  const { BusinessUnits, Product } = deploymentData || {};

  const isShowBuySubscriptionButton = useMemo(() => {
    if (
      BusinessUnits?.some((businessUnit) => businessUnit.ValidationHardware) &&
      !values?.BusinessUnitIds?.length
    ) {
      return false;
    }

    return !BusinessUnits?.some(
      (businessUnit) =>
        businessUnit.ValidationHardware &&
        values?.BusinessUnitIds?.includes(businessUnit.Id),
    );
  }, [BusinessUnits, values?.BusinessUnitIds]);

  const handleDoneClick = () => {
    form.validateFields().then(() => {
      form.submit();
    });
  };

  const handleButtonClick = () => {
    form.validateFields().then(() => {
      fetchChangeDrawerPage("cr-select");
    });
  };

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmittable(true))
      .catch(() => setSubmittable(false));
  }, [form, values]);

  if (!deploymentData || !hardwareItem) {
    return null;
  }

  return (
    <Flex vertical gap={12}>
      {isShowBuySubscriptionButton && (
        <ButtonLink
          block
          to={`/${localization.currentLang}/store?Product=${Product?.Id}`}
          size="large"
          type="default"
        >
          <LocalizationText value="modal.hardware.install.button.buy.subscription.text" />
        </ButtonLink>
      )}

      {!isCrSelect && (
        <Button
          block
          size="large"
          type="primary"
          onClick={handleDoneClick}
          disabled={!submittable}
        >
          <LocalizationText value="modal.hardware.install.button.done.text" />
        </Button>
      )}

      {isCrSelect && (
        <Button
          block
          size="large"
          type="primary"
          onClick={handleButtonClick}
          disabled={!submittable}
        >
          <LocalizationText value="modal.hardware.install.button.continue.text" />
        </Button>
      )}
    </Flex>
  );
};

export const ModalHardwareInstallBusinessUnitSelect = () => {
  const [search, setSearch] = useState<string>("");
  const localization = useLocalization();

  const { form, deploymentData, hardwareItem, fetchChangeDrawerPage } =
    useContextModalHardwareInstall();

  const { BusinessUnits, RequiredProducts, Accounts } = deploymentData || {};

  const getBusinessUnitStatusItems = (
    businessUnit: DeploymentBusinessUnitItemType,
  ): any => {
    if (businessUnit.ValidationHardware) {
      return [
        {
          type: "success",
          label: (
            <LocalizationText value="modal.hardware.install.label.license.text" />
          ),
        },
      ];
    }

    if (!businessUnit.ValidationHardware) {
      return [
        {
          type: "error",
          label: (
            <LocalizationText value="modal.hardware.install.label.not.assigned.text" />
          ),
        },
      ];
    }

    return null;
  };

  const businessUnitOptions = useMemo(
    () =>
      BusinessUnits?.map((item) => ({
        style: item.Name.toLowerCase().includes(search.toLowerCase())
          ? {}
          : { display: "none" },
        statusItems: getBusinessUnitStatusItems(item),
        value: item.Id,
        disabled: item.Products?.some(
          (product) => product.ProductId === hardwareItem?.Id,
        ),
        label: item.Name,
      })),
    [BusinessUnits, search],
  );

  const showButtonAdd: boolean = useMemo(() => {
    if (!RequiredProducts?.length) return true;
    return !!Accounts?.some((account) =>
      account.Licenses.some((license) => (license.AvailableCount || 0) > 0),
    );
  }, [RequiredProducts, Accounts]);

  useEffect(() => {
    form.setFieldValue("NewBusinessUnit", false);
  }, []);

  if (!deploymentData) {
    return null;
  }

  return (
    <Flex vertical align="stretch" gap={24}>
      <Flex vertical gap={12} align="stretch">
        <InputSearch
          size="large"
          value={search}
          onChange={({ target }) => setSearch(target.value || "")}
          placeholder={localization.getText(
            "modal.hardware.install.input.search.placeholder.text",
          )}
        />

        {showButtonAdd && (
          <Button
            block
            size="large"
            icon={<PlusIcon />}
            onClick={() => fetchChangeDrawerPage("business-unit-create")}
          >
            {
              <LocalizationText value="modal.hardware.install.button.create.business.unit.text" />
            }
          </Button>
        )}
      </Flex>

      <Flex vertical gap={12} align="stretch">
        <FormItem rules={[{ required: true }]} name="BusinessUnitIds">
          <CheckboxGroupList
            maxLength={1}
            options={businessUnitOptions ?? []}
          />
        </FormItem>
      </Flex>
    </Flex>
  );
};
